import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { X, Save, Settings2, Trash2, Plus, Search, Image as ImageIcon, Database, Eye, Edit3, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';
import { usePermission } from '../../context/PermissionContext';
import EavAttributeField from '../../components/EavAttributeField';
import M2MediaGallery from '../../components/M2MediaGallery';
import M2TierPriceEditor from '../../components/M2TierPriceEditor';
import { findFirstEavError, isAttributeRequired, renderAsterisk } from '../../utils/attributeValidation';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  return res.json();
};

/**
 * ChannelEavForm — 渠道产品创建/编辑/查看页
 *
 * 表单结构（按顺序）：
 *   1. Store 商店选择
 *   2. SKU（ERP SKU）
 *   3. Channel Type（magento2）
 *   4. Attribute Set 属性集
 *   5. EAV 属性平铺展示（无分组，排除图片/阶梯价/媒体相册字段）
 *   6. M2 Media Gallery（合并所有图片相关字段）
 *   7. M2 Tier Prices
 *   8. Variant Management（配置变体 + 添加已有变体）
 */
const ChannelEavForm = ({ viewOnly = false }) => {
  const { id } = useParams();
  const isEdit = !!id;
  const { t, lang } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();

  // 保存后回到列表时保留来源页码（编辑按钮带过来的 state）
  const backToList = () => {
    const { fromPage, fromPageSize } = location.state || {};
    const qs = new URLSearchParams();
    if (fromPage && fromPage > 1) qs.set('page', fromPage);
    if (fromPageSize && fromPageSize !== 20) qs.set('pageSize', fromPageSize);
    const q = qs.toString();
    navigate('/channel/eav-products' + (q ? '?' + q : ''));
  };

  const [stores, setStores] = useState([]);
  const [attributeSets, setAttributeSets] = useState([]);
  const [eavConfig, setEavConfig] = useState([]);
  const [standardCodes, setStandardCodes] = useState({});
  const [skuError, setSkuError] = useState('');
  const [skuChecking, setSkuChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(viewOnly);

  useEffect(() => { setViewOnlyMode(viewOnly); }, [viewOnly]);

  // ── Basic form data ──
  const [formData, setFormData] = useState({
    store_id: '',
    sku: '',
    channel_type: 'magento2',
    attribute_set_id: '',
    website_ids: null,
    attributes: {},
    stock_item: { qty: 9999, is_in_stock: true },
    category_name: '',
  });

  // ── Variant state ──
  const [showVariantConfig, setShowVariantConfig] = useState(false);
  const [showAddVariants, setShowAddVariants] = useState(false);
  const [showUpdateVariantAttrs, setShowUpdateVariantAttrs] = useState(false);
  const [selectedUpdateAttrs, setSelectedUpdateAttrs] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingTierPrices, setPendingTierPrices] = useState([]);
  const [variantDefiningAttrs, setVariantDefiningAttrs] = useState([]);
  const [variantAttrOptions, setVariantAttrOptions] = useState({});
  const [variantValues, setVariantValues] = useState({});
  const [variantAttrs, setVariantAttrs] = useState([]); // 店铺实例下所有可选属性（含选项）
  // ── Variant image state ──
  const [variantImageAttr, setVariantImageAttr] = useState('');
  const [variantImages, setVariantImages] = useState({}); // { [option_value]: [image_urls] }
  const originalChildEntityIdsRef = useRef(new Set());

  // ── Add existing variants ──
  const [variantSearch, setVariantSearch] = useState('');
  const [variantCandidates, setVariantCandidates] = useState([]);
  const [selectedAddEntityIds, setSelectedAddEntityIds] = useState([]);

  // ── EAV 属性：从内联展示排除的字段 ──
  // 这些字段由独立组件处理（M2MediaGallery / M2TierPriceEditor），或已作为顶级字段存在
  // 注意：M2 同步过来的属性有 m2_{storeId}_ 前缀，需要同时匹配裸名和带前缀的版本
  const imageAttrCodes = new Set([
    'image', 'small_image', 'thumbnail', 'swatch_image', 'media_gallery',
    'image_label', 'small_image_label', 'thumbnail_label',
  ]);
  const skipAttrCodes = new Set([
    'sku',
    'tier_price',
    'custom_layout_update',
    'price_view', // bundle-only, 不支持捆绑产品
    'quantity_and_stock_status', // 由独立库存组件处理
    // Bundle Product 专属（暂不支持）
    'shipment_type', 'price_type', 'sku_type', 'weight_type',
    // Downloadable Product 专属（暂不支持）
    'links_title', 'links_purchased_separately', 'links_exist', 'samples_title',
    // 系统自动处理/只读（M2 自动管理，无需展示）
    'has_options', 'required_options', 'old_id',
    'url_path', 'created_at', 'updated_at', 'gallery',
    'minimal_price', // M2 价格索引自动计算，只读隐藏
  ]);

  // 构建完整排除集：包含裸名 + 带 m2_{storeId}_ 前缀的版本（依赖 store_id 动态生成）
  // sku 直接映射到 entity_channel.sku，不创建 EAV 属性
  const _baseSkipCodes = useMemo(() => new Set([...imageAttrCodes, ...skipAttrCodes]), []);
  const allSkippedAttrCodes = useMemo(() => {
    const s = new Set([..._baseSkipCodes]);
    if (formData.store_id) {
      _baseSkipCodes.forEach(code => {
        s.add(`m2_${formData.store_id}_${code}`);
      });
    }
    return s;
  }, [formData.store_id, _baseSkipCodes]);

  // ── Load stores ──
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/channel/stores');
        if (res.status === 'success') setStores(res.data.stores || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ── Load attribute sets ──
  const fetchAttributeSets = useCallback(async (channelType, storeId) => {
    try {
      let url = `/api/channel/eav/attribute-sets?channel_type=${channelType}`;
      if (storeId) url += `&store_id=${storeId}`;
      const res = await apiFetch(url);
      if (res.status === 'success') {
        const sets = res.data || [];
        setAttributeSets(sets);
        return sets;
      }
    } catch (e) { /* ignore */ }
    return [];
  }, []);

  // ── 仅在新增模式自动设置默认属性集 ──
  useEffect(() => {
    if (isEdit) return;
    (async () => {
      const sets = await fetchAttributeSets(formData.channel_type || 'magento2', formData.store_id);
      if (sets.length > 0) {
        setFormData(prev => {
          if (!prev.attribute_set_id) {
            return { ...prev, attribute_set_id: String(sets[0].origin_id || sets[0].set_id) };
          }
          return prev;
        });
      }
    })();
  }, [formData.channel_type, formData.store_id, fetchAttributeSets, isEdit]);

  // ── Load EAV form config ──
  const loadConfigForSet = useCallback(async (setId, sets, storeId) => {
    if (!setId || setId === '') { setEavConfig([]); return; }
    defaultsAppliedRef.current = false;
    // 如果传入的是 M2 原始 ID（origin_id），映射为系统本地 set_id
    const rawId = typeof setId === 'string' ? parseInt(setId, 10) : setId;
    const matched = (sets || []).find(s => parseInt(s.origin_id, 10) === rawId);
    const localSetId = matched ? matched.set_id : rawId;
    try {
      const sid = storeId || formData.store_id;
      const url = `/api/channel/eav/products/form-config?attribute_set_id=${localSetId}`;
      const res = await apiFetch(sid ? url + `&store_id=${sid}` : url);
      if (res.status === 'success') {
        setEavConfig(res.data || []);
        if (res.standard_codes) setStandardCodes(res.standard_codes);
      }
    } catch (e) { console.error('Load EAV config failed:', e); }
  }, [formData.store_id]);

  // ── 加载店铺实例下所有可选属性（用于变体配置） ──
  const loadVariantAttrs = useCallback(async (storeId, attrSetId) => {
    if (!storeId || storeId === '' || isNaN(storeId)) { setVariantAttrs([]); return; }
    try {
      // attrSetId 可能为 M2 origin_id，转换为本地 set_id
      const rawSetId = typeof attrSetId === 'string' ? parseInt(attrSetId, 10) : attrSetId;
      const localSetId = attributeSets.length > 0 && rawSetId
        ? ((attributeSets.find(s => parseInt(s.origin_id, 10) === rawSetId) || {}).set_id || rawSetId)
        : rawSetId;
      let url = `/api/channel/eav/products/variant-attrs?store_id=${storeId}`;
      if (localSetId) url += `&attribute_set_id=${localSetId}`;
      const res = await apiFetch(url);
      if (res.status === 'success') setVariantAttrs(res.data || []);
    } catch (e) { console.error('Load variant attrs failed:', e); }
  }, [attributeSets]);

  // 根据 store_id 动态获取带前缀的属性 code（如 store_id=2 → m2_2_name）
  const prefixedCode = useCallback((rawCode, storeId) => {
    const sid = storeId || formData.store_id;
    if (!sid) return rawCode;
    return `m2_${sid}_${rawCode}`;
  }, [formData.store_id]);

  useEffect(() => {
    if (formData.attribute_set_id) {
      loadConfigForSet(parseInt(formData.attribute_set_id, 10), attributeSets);
    }
  }, [formData.attribute_set_id, loadConfigForSet, attributeSets]);

  // ── 新增时注入 M2 属性的默认值 ──
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (isEdit || eavConfig.length === 0) return;
    // 只在属性集第一次加载时注入默认值
    if (defaultsAppliedRef.current) return;

    const defaults = {};
    for (const attr of eavConfig) {
      if (attr.default_value !== null && attr.default_value !== undefined && attr.default_value !== '') {
        defaults[attr.attribute_code] = attr.default_value;
      }
    }
    if (Object.keys(defaults).length > 0) {
      setFormData(prev => ({
        ...prev,
        attributes: { ...prev.attributes, ...defaults }
      }));
      defaultsAppliedRef.current = true;
    }
  }, [eavConfig, isEdit]);

  // ── 店铺 ID 变化时重新加载变体可选属性 ──
  useEffect(() => {
    const storeId = parseInt(formData.store_id, 10);
    if (storeId && !isNaN(storeId)) {
      loadVariantAttrs(storeId, formData.attribute_set_id ? parseInt(formData.attribute_set_id, 10) : undefined);
    }
  }, [formData.store_id, loadVariantAttrs]);

  // ── Load existing data ──
  useEffect(() => {
    if (!isEdit) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await apiFetch(`/api/channel/eav/products/${id}`);
        if (res.status === 'success' && res.data) {
          const p = res.data;
          const loadedAttrs = p.attributes || {};
          const loadedEntityId = p.entity_id ? parseInt(p.entity_id, 10) : 0;
          setFormData({
            store_id: String(p.store_id || ''),
            sku: p.sku || '',
            channel_type: p.channel_type || 'magento2',
            // attribute_set_id 在编辑模式下由下方匹配 origin_id 的逻辑重新赋值
            attribute_set_id: '',
            website_ids: p.website_ids || null,
            attributes: loadedAttrs,
            stock_item: p.stock_item || { qty: 0, is_in_stock: false },
            entity_id: loadedEntityId,
            _productType: p.product_type || 'simple',
            category_name: p.category_name || '',
          });
          // Load variants if configurable parent
          if (p.product_type === 'configurable' && p.children && p.children.length > 0) {
            const childEntityIds = p.children.map(c => c.entity_id);
            originalChildEntityIdsRef.current = new Set(childEntityIds);

            setCombinations(p.children.map((c, i) => ({
              id: i,
              _ref: c.entity_id,
              _isExisting: true,
              sku: c.sku,
              name: (c.attributes?.[standardCodes.name || prefixedCode('name', p.store_id)] || c.attributes?.name || c.name || ''),
              price: c.attributes?.[standardCodes.price || prefixedCode('price', p.store_id)] || 0,
              images: c.image ? [c.image] : [],
              attributes: c.attributes || {}

            })));

            // 使用数据库已保存的 variant_attributes
            if (p.variant_attributes && Array.isArray(p.variant_attributes) && p.variant_attributes.length > 0) {
              setVariantDefiningAttrs(p.variant_attributes.map(code => ({ code, label: code })));
            }
          }

          // 编辑模式：匹配 entity_channel 中存的 origin_id，回显正确的属性集
          // p.attribute_set_id 存的是 M2 origin_id，如 9=Clothing
          // 需在 attributeSets 中找到 origin_id 与之匹配的那一项
          let effectiveSetId = null;
          let attributeSetsData = null;
          if (p.channel_type) {
            attributeSetsData = await fetchAttributeSets(p.channel_type, p.store_id);
            if (attributeSetsData.length > 0) {
              const storedOriginId = p.attribute_set_id ? parseInt(p.attribute_set_id, 10) : null;
              const matchedSet = storedOriginId
                ? attributeSetsData.find(s => parseInt(s.origin_id, 10) === storedOriginId)
                : null;
              effectiveSetId = matchedSet
                ? storedOriginId
                : parseInt(attributeSetsData[0].origin_id || attributeSetsData[0].set_id, 10);
            }
          }
          if (effectiveSetId) {
            setFormData(prev => ({ ...prev, attribute_set_id: String(effectiveSetId) }));
            loadConfigForSet(effectiveSetId, attributeSetsData, p.store_id);
          } else if (p.attribute_set_id) {
            loadConfigForSet(p.attribute_set_id, undefined, p.store_id);
          }
        }
      } catch (e) { console.error('Load product failed:', e); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit, fetchAttributeSets, loadConfigForSet]);

  // ── 变体属性选中时，从 variantAttrs 提取选项（不再调 settings API） ──
  useEffect(() => {
    if (variantAttrs.length === 0 || variantDefiningAttrs.length === 0) return;
    const newOptions = { ...variantAttrOptions };
    let changed = false;
    variantDefiningAttrs.forEach(v => {
      if (newOptions[v.code]) return;
      const attr = variantAttrs.find(a => a.code === v.code);
      if (!attr || !attr.options || attr.options.length === 0) {
        newOptions[v.code] = attr?.options || [];
        changed = true;
        return;
      }
      newOptions[v.code] = attr.options.map(opt => ({
        ...opt,
        label: opt.label || opt.value || '',
      }));
      changed = true;
    });
    if (changed) setVariantAttrOptions(newOptions);
  }, [variantDefiningAttrs, variantAttrs, variantAttrOptions]);

  // ── Update variantDefiningAttrs labels from variantAttrs ──
  useEffect(() => {
    if (variantAttrs.length > 0 && variantDefiningAttrs.length > 0) {
      setVariantDefiningAttrs(prev => prev.map(v => {
        const matched = variantAttrs.find(a => a.code === v.code);
        return matched ? { ...v, label: matched.label || v.code } : v;
      }));
    }
  }, [variantAttrs, variantDefiningAttrs]);

  // ── EAV attribute change ──
  const handleAttributeChange = (code, val) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [code]: val },
    }));
  };

  // ── Variant option toggle ──
  const toggleVariantOption = (attrCode, optionValue) => {
    setVariantValues(prev => {
      const current = prev[attrCode] || [];
      if (current.includes(optionValue)) {
        return { ...prev, [attrCode]: current.filter(v => v !== optionValue) };
      }
      return { ...prev, [attrCode]: [...current, optionValue] };
    });
  };

  // ── Generate combinations ──
  const generateCombinations = useCallback(() => {
    const keys = Object.keys(variantValues);
    if (keys.length === 0) return [];
    const result = [];
    const helper = (index, current) => {
      if (index === keys.length) { result.push({ ...current }); return; }
      const key = keys[index];
      const vals = variantValues[key] || [];
      vals.forEach(val => helper(index + 1, { ...current, [key]: val }));
    };
    helper(0, {});
    return result;
  }, [variantValues]);

  // ── Configure variants confirm ──
  const handleConfirmVariants = () => {
    const generated = generateCombinations();
    if (generated.length === 0) {
      notify(t('channel.variant_no_combination'), 'warning');
      return;
    }

    const existingFingerprints = new Set(
      combinations.map(c =>
        variantDefiningAttrs.map(a => `${a.code}:${c.attributes[a.code]}`).sort().join('|')
      )
    );

    const baseSku = formData.sku?.trim() || 'variant';
    let newCombos = [];
    let nextId = Math.max(0, ...combinations.map(c => c.id)) + 1;

    generated.forEach(combo => {
      const fp = variantDefiningAttrs.map(a => `${a.code}:${combo[a.code]}`).sort().join('|');
      if (existingFingerprints.has(fp)) return;
      const suffix = variantDefiningAttrs.map(a => {
        const opt = (variantAttrOptions[a.code] || []).find(o => o.value === combo[a.code]);
        return opt ? opt.label : combo[a.code];
      }).join('-');
      const variantSku = `${baseSku}-${suffix}`.replace(/[^a-zA-Z0-9\-_]/g, '');
      const nameSuffix = variantDefiningAttrs.map(a => {
        const opt = (variantAttrOptions[a.code] || []).find(o => o.value === combo[a.code]);
        return opt ? opt.label : combo[a.code];
      }).join(' ');

      newCombos.push({
        id: nextId++,
        _ref: null,
        _isExisting: false,
        _newlyAdded: true,
        sku: variantSku,
        name: `${formData.attributes[standardCodes.name || prefixedCode('name')] || ''} ${nameSuffix}`.trim(),
        attributes: { ...combo },
        images: (variantImageAttr && variantImages[combo[variantImageAttr]])
          ? [...variantImages[combo[variantImageAttr]]]
          : [],
      });
    });

    if (newCombos.length === 0) {
      notify(t('channel.variant_all_exist'), 'warning');
      return;
    }
    setCombinations(prev => [...prev, ...newCombos]);
    setShowVariantConfig(false);
    notify(t('channel.variant_generated', { count: newCombos.length }), 'success');
  };

  // ── Add existing variants ──
  const handleSearchCandidates = useCallback(async () => {
    const storeId = parseInt(formData.store_id, 10);
    if (!storeId) { notify(t('channel.please_select_store'), 'warning'); return; }
    try {
      const params = new URLSearchParams();
      params.set('store_id', storeId);
      if (variantSearch) params.set('search', variantSearch);
      params.set('limit', '50');
      const res = await apiFetch(`/api/channel/eav/products/simple-candidates?${params.toString()}`);
      if (res.status === 'success') setVariantCandidates(res.data || []);
    } catch (e) { /* ignore */ }
  }, [formData.store_id, variantSearch, notify]);

  const handleConfirmAddVariants = () => {
    const existingIds = new Set(combinations.map(c => c._ref).filter(Boolean));
    const toAdd = variantCandidates.filter(c => selectedAddEntityIds.includes(c.entity_id) && !existingIds.has(c.entity_id));
    if (toAdd.length === 0) { notify(t('channel.no_new_variants_selected'), 'warning'); return; }
    const nextId = Math.max(0, ...combinations.map(c => c.id)) + 1;
    const newCombos = toAdd.map((c, i) => ({
      id: nextId + i,
      _ref: c.entity_id,
      _isExisting: true,
      _newlyAdded: true,
      sku: c.sku,
      name: (c.attributes?.[standardCodes.name || prefixedCode('name')] || c.attributes?.name || c.name || ''),
      price: c.attributes?.[standardCodes.price || prefixedCode('price')] || 0,
      images: c.images || [],
      attributes: c.attributes || {}

    }));
    setCombinations(prev => [...prev, ...newCombos]);
    setSelectedAddEntityIds([]);
    setShowAddVariants(false);
    notify(t('channel.variants_added', { count: newCombos.length }), 'success');
  };

  const removeCombination = (id) => {
    setCombinations(prev => prev.filter(c => c.id !== id));
  };

  // ── Variant validation helpers ──
  const getExistingVariantFingerprints = () => {
    if (variantDefiningAttrs.length === 0) return new Set();
    return new Set(
      combinations
        .filter(item => variantDefiningAttrs.every(a => item.attributes && item.attributes[a.code]))
        .map(item =>
          variantDefiningAttrs
            .map(a => `${a.code}:${item.attributes[a.code]}`)
            .sort()
            .join('|')
        )
    );
  };

  const isDuplicateVariant = (p) => {
    if (variantDefiningAttrs.length === 0 || !p.attributes) return true;
    const fingerprints = getExistingVariantFingerprints();
    const candidateFp = variantDefiningAttrs
      .map(a => `${a.code}:${p.attributes[a.code]}`)
      .sort()
      .join('|');
    return fingerprints.has(candidateFp);
  };

  const isProductValidForVariant = (p) => {
    if (variantDefiningAttrs.length === 0) return false;
    if (!p.attributes) return false;
    for (const attr of variantDefiningAttrs) {
      const childVal = p.attributes[attr.code];
      if (!childVal || childVal === '') return false;
    }
    if (isDuplicateVariant(p)) return false;
    return true;
  };

  const getProductInvalidReason = (p) => {
    if (variantDefiningAttrs.length === 0) return t('validation.variant_attrs_required');
    if (!p.attributes) return t('validation.variant_attrs_missing');
    for (const attr of variantDefiningAttrs) {
      const childVal = p.attributes[attr.code];
      if (!childVal || childVal === '') {
        return t('validation.variant_attr_missing', {label: attr.label || attr.code});
      }
    }
    if (isDuplicateVariant(p)) return t('validation.variant_duplicate');
    return '';
  };


  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    const skuVal = formData.sku?.trim() || '';
    if (!formData.store_id || !skuVal) {
      notify(t('common.fill_required'), 'error');
      return;
    }

    // 动态验证所有 is_required=1 的 EAV 属性 + input_validation 格式验证
    if (eavConfig.length > 0 && formData.attributes) {
      // 变体场景下父产品的某些属性移到子产品，父产品无需填写
      const isParentWithVariants = combinations.length > 0;
      const hiddenForParentCodes = isParentWithVariants
        ? [
            // 变体定义属性（color, size 等）无需父产品填写
            ...variantDefiningAttrs.map(v => v.code).filter(Boolean),
            // 仅 price 属性由子产品管理，父产品设为 0，其他 Price 类型属性不动
            ...eavConfig
              .filter(a => a.is_price)
              .map(a => a.attribute_code)
              .filter(Boolean),
          ]
        : [];

      const eavError = findFirstEavError(eavConfig, formData.attributes, t, {
        excludeCodes: [...hiddenForParentCodes, ...allSkippedAttrCodes],
        getLabel: getLocalizedLabel,
      });
      if (eavError) {
        notify(eavError, 'error');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const basePayload = {
        channel_type: formData.channel_type,
        sku: skuVal,
        store_id: parseInt(formData.store_id, 10),
        attribute_set_id: formData.attribute_set_id ? parseInt(formData.attribute_set_id, 10) : undefined,
        website_ids: formData.website_ids,
        attributes: { ...formData.attributes },
      };

      // 父产品 price 未填值则自动设为 0（仅针对映射到的那个 price 属性）
      if (combinations.length > 0) {
        eavConfig
          .filter(a => a.is_price)
          .forEach(pa => {
            const val = basePayload.attributes[pa.attribute_code];
            if (val === null || val === undefined || val === '') {
              basePayload.attributes[pa.attribute_code] = '0';
            }
          });
      }

      if (combinations.length > 0 && variantDefiningAttrs.length > 0) {
        basePayload.variant_attributes = variantDefiningAttrs.map(v => v.code);
      }
      // SKU 已映射到 entity_channel.sku，不再创建 EAV 属性

      // Stock 数据作为顶层字段，不经过 EAV
      if (formData.stock_item) {
        basePayload.stock_item = {
          qty: formData.stock_item.qty ?? 0,
          is_in_stock: formData.stock_item.is_in_stock !== false,
        };
      }



      // 附加上传的图片（新建模式）
      if (!isEdit && pendingImages.length > 0) {
        basePayload.pending_media = pendingImages.map(img => ({
          url: img.url || img.file_path,
          types: img.types || [],
          label: img.label || '',
          position: img.position,
        }));
      }

      // 附加 tier prices（新建模式）
      if (!isEdit && pendingTierPrices.length > 0) {
        basePayload.pending_tier_prices = pendingTierPrices.map(item => ({
          customer_group_id: parseInt(item.customer_group_id, 10),
          customer_group_code: item.customer_group_code || 'NOT LOGGED IN',
          qty: parseFloat(item.qty) || 0,
          price: parseFloat(item.price) || 0,
          price_type: item.price_type || 'fixed',
          website_id: parseInt(item.website_id, 10) || 0,
        }));
      }

      if (!isEdit && combinations.length > 0) {
        const parentRef = 'p0';
        const variantDefCodes = variantDefiningAttrs.map(v => v.code);
        // 子产品从父产品继承的属性（排除变体定义属性 + price）
        const inheritAttrs = Object.fromEntries(
          Object.entries(basePayload.attributes).filter(([k]) =>
            !variantDefCodes.includes(k) && !eavConfig.some(a => a.is_price && a.attribute_code === k)
          )
        );
        const payload = [
          { _ref: parentRef, ...basePayload },
          ...combinations.map(c => ({
            _ref: `c${c.id}`,
            sku: c.sku,
            product_name: c.name,
            store_id: parseInt(formData.store_id, 10),
            attribute_set_id: basePayload.attribute_set_id,
            item_group_id: parentRef,
            channel_type: formData.channel_type,
            website_ids: formData.website_ids,
            attributes: {
              // 1. 继承父产品非变体字段
              ...inheritAttrs,
              // 2. 子产品自己的变体属性值
              ...(c.attributes || {}),
              // 3. 子产品特有字段
              [standardCodes.name || prefixedCode('name')]: c.name,
              [standardCodes.price || prefixedCode('price')]: c.price || 0,
            },
            pending_media: c.images && c.images.length > 0
              ? c.images.map((img, idx) => ({
                  url: typeof img === 'string' ? img : (img.url || img.file_path),
                  types: [],
                  label: '',
                  position: idx,
                }))
              : undefined,
            stock_item: basePayload.stock_item,
          })),
        ];
        const res = await apiFetch('/api/channel/eav/products/add', {
          method: 'POST', body: JSON.stringify(payload),
        });
        if (res.status === 'success') {
          notify(t('channel.create_success_with_variants', { count: combinations.length }), 'success');
          backToList();
        } else { notify(res.message || t('common.error'), 'error'); }
      } else if (isEdit) {
        const newChildEntityIds = combinations.filter(c => c._isExisting).map(c => c._ref).filter(Boolean);
        const currentIds = new Set(newChildEntityIds);
        const originalIds = originalChildEntityIdsRef.current;
        const removedChildren = [...originalIds].filter(cid => !currentIds.has(cid));
        const addedChildren = [...currentIds].filter(cid => !originalIds.has(cid));
        const payload = { ...basePayload, entity_id: parseInt(id, 10) };
        if (addedChildren.length > 0) payload.add_children = addedChildren;
        if (removedChildren.length > 0) payload.remove_children = removedChildren;
        const newVariants = combinations.filter(c => !c._isExisting);
        const variantDefCodes = variantDefiningAttrs.map(v => v.code);
        const inheritAttrs = Object.fromEntries(
          Object.entries(basePayload.attributes).filter(([k]) =>
            !variantDefCodes.includes(k) && !eavConfig.some(a => a.is_price && a.attribute_code === k)
          )
        );
        if (newVariants.length > 0) {
          payload.new_children = newVariants.map(c => ({
            sku: c.sku, product_name: c.name,
            store_id: parseInt(formData.store_id, 10),
            attribute_set_id: basePayload.attribute_set_id,
            item_group_id: parseInt(id, 10),
            channel_type: formData.channel_type,
            website_ids: formData.website_ids,
            attributes: {
              ...inheritAttrs,
              ...(c.attributes || {}),
              [standardCodes.name || prefixedCode('name')]: c.name,
              [standardCodes.price || prefixedCode('price')]: c.price || 0,
            },
            stock_item: basePayload.stock_item,
            pending_media: c.images && c.images.length > 0
              ? c.images.map((img, idx) => ({
                  url: typeof img === 'string' ? img : (img.url || img.file_path),
                  types: [],
                  label: '',
                  position: idx,
                }))
              : undefined,
          }));
        }
        // 全量更新已有子产品（包含变体表格中已编辑的值）
        const existingUpdated = combinations.filter(c => c._isExisting);
        if (existingUpdated.length > 0) {
          payload.update_children = existingUpdated.map(c => ({
            entity_id: parseInt(c._ref, 10),
            sku: c.sku,
            product_name: c.name,
            store_id: parseInt(formData.store_id, 10),
            attribute_set_id: basePayload.attribute_set_id,
            channel_type: formData.channel_type,
            website_ids: formData.website_ids,
            attributes: {
              ...inheritAttrs,
              ...(c.attributes || {}),
              [standardCodes.name || prefixedCode('name')]: c.name,
              [standardCodes.price || prefixedCode('price')]: c.price || 0,
            },
            pending_media: c.images && c.images.length > 0
              ? c.images.map((img, idx) => ({
                  url: typeof img === 'string' ? img : (img.url || img.file_path),
                  types: [],
                  label: '',
                  position: idx,
                }))
              : undefined,
          }));
        }
        const res = await apiFetch(`/api/channel/eav/products/${id}/edit`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
        if (res.status === 'success') {
          notify(t('common.update_success'), 'success');
          backToList();
        } else { notify(res.message || t('common.error'), 'error'); }
      } else {
        const res = await apiFetch('/api/channel/eav/products/add', {
          method: 'POST', body: JSON.stringify(basePayload),
        });
        if (res.status === 'success') {
          notify(t('common.create_success'), 'success');
          backToList();
        } else { notify(res.message || t('common.error'), 'error'); }
      }
    } catch (err) {
      notify('Submit failed: ' + (err.message || t('common.error')), 'error');
    } finally { setIsSubmitting(false); }
  };

  // ── Get localized label for an attribute ──
  const getLocalizedLabel = useCallback((attr) => {
    if (!attr) return '';
    // 1. 用户设置的什么语言，就展示什么语言
    let label = attr.labels?.find(l => l.locale === lang)?.label || '';
    // 2. 无精确匹配时，尝试 locale 前缀匹配（如 'en' 匹配 'en_US'）
    if (!label && lang) {
      label = attr.labels?.find(l => l.locale && l.locale.startsWith(lang + '_'))?.label || '';
    }
    // 3. 仍无匹配时，尝试 null-locale 的标签（M2 同步属性的 locale 为 null）
    if (!label) label = attr.labels?.find(l => !l.locale)?.label || '';
    // 4. 最终回退：default_label 或 attribute_code
    if (!label) label = attr.default_label || attr.attribute_code || '';
    // 清理因唯一性约束追加的数字后缀
    return label.replace(/\d+$/, '');
  }, [lang]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Filter out skipped attributes from inline EAV display
  const inlineEavAttrs = eavConfig.filter(a => !allSkippedAttrCodes.has(a.attribute_code));

  return (
    <div className="space-y-6">
      {/* ── Title bar ── */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {isEdit
              ? (viewOnlyMode ? t('channel.view_product') : t('channel.edit_product'))
              : t('channel.new_product')}
          </h1>
        </div>
        <button onClick={() => navigate(-1)}
          className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
          <X size={20} />
        </button>
      </div>

      {/* ── Form ── */}
      <form onSubmit={handleSubmit}
        className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">

        {/* ═══ Store ═══ */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('channel.store')} <span className="text-red-500">*</span>
          </label>
          <select value={formData.store_id}
            onChange={e => {
              const storeId = e.target.value;
              const store = stores.find(s => String(s.auth_id) === storeId);
              const websiteIds = store?.website_id ? [store.website_id] : null;
              setFormData(f => ({ ...f, store_id: storeId, website_ids: websiteIds }));
            }}
            disabled={viewOnlyMode}
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200">
            <option value="">-- {t('common.select')} --</option>
            {stores.map(s => (
              <option key={s.auth_id} value={s.auth_id}>{s.store_name}</option>
            ))}
          </select>
        </div>

        {/* ═══ Channel Type ═══ */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.channel_type')}</label>
          <select value={formData.channel_type}
            onChange={e => {
              const ct = e.target.value;
              setFormData(f => ({ ...f, channel_type: ct, attribute_set_id: '' }));
              fetchAttributeSets(ct);
            }}
            disabled={viewOnlyMode}
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200">
            <option value="magento2">Magento 2</option>
          </select>
        </div>

        {/* ═══ Attribute Set ═══ */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.attribute_set')}</label>
          <select value={formData.attribute_set_id}
            onChange={e => {
              const setId = e.target.value;
              setFormData(f => ({ ...f, attribute_set_id: setId }));
              if (setId) loadConfigForSet(parseInt(setId, 10));
            }}
            disabled={viewOnlyMode}
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200">
            <option value="">-- {t('common.select')} --</option>
            {attributeSets.map(s => (
              <option key={s.origin_id || s.set_id} value={s.origin_id || s.set_id}>{s.set_name}</option>
            ))}
          </select>
        </div>

        {/* ═══ SKU — 直接映射到 entity_channel.sku ═══ */}
        <div className="pt-4 pb-4 space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            SKU <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={formData.sku || ''}
            onChange={e => { setSkuError(''); setFormData(f => ({ ...f, sku: e.target.value })); }}
            onBlur={async () => {
              if (!formData.sku?.trim() || isEdit || !formData.store_id) return;
              setSkuChecking(true);
              setSkuError('');
              try {
                const res = await fetch('/api/channel/eav/products/check-sku?sku=' + encodeURIComponent(formData.sku.trim()) + '&store_id=' + formData.store_id);
                const data = await res.json();
                if (data.status === 'success' && data.exists) {
                  setSkuError('SKU already exists in this store');
                }
              } catch (e) { /* ignore */ }
              setSkuChecking(false);
            }}
            disabled={viewOnlyMode}
            placeholder="Product SKU"
            className={`w-full p-2 border ${skuError ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-700'} rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200`}
          />
          {skuError && <p className="text-xs text-red-500 mt-1">{skuError}</p>}
        </div>

        {/* ═══ ERP Product ID (read-only) — product_id from entity_product ═══ */}
        {(formData.product_id || formData.erp_sku) && (
          <div className="pt-4 pb-4 space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('channel.erp_product') || 'ERP Product'}
            </label>
            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Database size={16} className="text-blue-500 shrink-0" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  #{formData.product_id}
                </span>
                {formData.erp_sku && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    ({formData.erp_sku})
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══ Stock (单仓库模式) — 直接写入 M2 stockItem API ═══ */}
        {formData.channel_type === 'magento2' && (
          <div className="pt-4 pb-4 space-y-3">
            {/* 库存数量 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('m2_stock.qty') || 'Quantity'}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.stock_item?.qty ?? ''}
                onChange={e => setFormData(f => ({
                  ...f,
                  stock_item: { ...f.stock_item, qty: e.target.value === '' ? null : parseFloat(e.target.value) }
                }))}
                disabled={viewOnlyMode}
                placeholder="0"
                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
              />
            </div>
            {/* 库存状态 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('m2_stock.status') || 'Stock Status'}
              </label>
              <select
                value={formData.stock_item?.is_in_stock === false ? '0' : '1'}
                onChange={e => setFormData(f => ({
                  ...f,
                  stock_item: { ...f.stock_item, is_in_stock: e.target.value === '1' }
                }))}
                disabled={viewOnlyMode}
                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
              >
                <option value="1">{t('channel.in_stock')}</option>
                <option value="0">{t('channel.out_of_stock')}</option>
              </select>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
              {t('m2_stock.single_warehouse_hint') || 'Qty + stock status synced to Magento 2 via stockItems API (single warehouse)'}
            </p>
          </div>
        )}

        {/* ═══ EAV Attributes — 单列平铺展示 ═══ */}
        {inlineEavAttrs.length > 0 && (
          <div className="pt-4 space-y-4">
            {inlineEavAttrs.map(attr => {
            // url_key 由系统自动管理，禁止编辑
            const isUrlKeyField = attr.attribute_code.replace(/^m2_\d+_/, '') === 'url_key';
            return (
              <div key={attr.attribute_code} className="space-y-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {getLocalizedLabel(attr)}
                  {renderAsterisk(attr.is_required)}
                </label>
                <EavAttributeField
                  attr={attr}
                  value={
                    attr.input_type === 'CategoryTree' && viewOnlyMode
                      ? (formData.category_name || formData.attributes[attr.attribute_code] || '')
                      : (formData.attributes[attr.attribute_code] ?? '')
                  }
                  onChange={(code, val) => handleAttributeChange(code, val)}
                  viewOnly={viewOnlyMode || isUrlKeyField}
                  hideLabel={true}
                  channelStoreId={parseInt(formData.store_id, 10) || 0}
                  displayName={formData.category_name}
                />
                {/* M2 Tier Prices — 紧跟在 M2 msrp_display_actual_price_type 后面 */}
                {formData.channel_type === 'magento2' && !viewOnlyMode &&
                  (attr.attribute_code.replace(/^m2_\d+_/, '') === 'msrp_display_actual_price_type') && (
                  <div className="pt-4 pb-2 space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
                      <div className="w-1 h-4 bg-green-500 rounded-full"></div>
                      {t('m2_tier_price.tier_prices')}
                    </label>
                    <M2TierPriceEditor
                      entityId={isEdit && formData.entity_id ? parseInt(formData.entity_id, 10) : null}
                      viewOnly={false}
                      storeId={formData.store_id}
                      onPricesChange={!id ? setPendingTierPrices : undefined}
                    />
                  </div>
                )}
                {/* Variant Config — 紧跟在 M2 name 字段后面，simple类型不显示 */}
                {formData._productType !== "simple" && (combinations.length > 0 || (!viewOnlyMode && eavConfig.length > 0)) &&
                  ((standardCodes.name ? attr.attribute_code === standardCodes.name : attr.attribute_code.replace(/^m2_\d+_/, '') === 'name')) && (
                  <div className="pt-4 pb-2 space-y-2">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-1 h-4 bg-amber-500 rounded-full"></div>
                      <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300">
                        {t('channel.variant_config')}
                      </h3>
                      {!viewOnlyMode && (
                        <div className="flex gap-2 ml-auto">
                          <button type="button" onClick={() => { setShowVariantConfig(true); const sid = parseInt(formData.store_id, 10); if (sid) { const asid = formData.attribute_set_id ? parseInt(formData.attribute_set_id, 10) : undefined; loadVariantAttrs(sid, asid); } }}
                            disabled={isEdit}
                            className="px-3 py-1.5 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                            <Settings2 size={14} />
                            {t('channel.configure_variants')}
                          </button>
                          <button type="button" onClick={() => { setShowAddVariants(true); handleSearchCandidates(); }}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center gap-1">
                            <Plus size={14} />
                            {t('channel.add_existing_variants')}
                          </button>
                          {isEdit && combinations.length > 0 && hasPermission && hasPermission('channel_products', 'update_variant_attributes') && (
                            <button type="button" onClick={() => {
                              setSelectedUpdateAttrs(variantDefiningAttrs.map(a => a.code));
                              setShowUpdateVariantAttrs(true);
                            }}
                              className="px-3 py-1.5 text-xs font-medium bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 border border-violet-200 dark:border-violet-700 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors flex items-center gap-1">
                              <Settings2 size={14} />
                              {t('channel.update_variant_attrs') || '更新变体属性'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {/* 变体属性标签 + 未知警告 */}
                    {combinations.length > 0 && variantDefiningAttrs.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {t('channel.variant_attrs') || '变体属性'}:
                        </span>
                        {variantDefiningAttrs.map(a => (
                          <span key={a.code} className="px-2.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full border border-blue-200 dark:border-blue-800">
                            {a.label || a.code}
                          </span>
                        ))}
                      </div>
                    )}
                    {combinations.length > 0 && variantDefiningAttrs.length === 0 && (
                      <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <AlertTriangle size={16} className="text-amber-500 shrink-0" />
                        <div className="text-sm text-amber-700 dark:text-amber-400">
                          <p className="font-medium">{t('channel.variant_attrs_unknown') || '变体属性未知'}</p>
                          <p className="text-xs mt-0.5 opacity-80">{t('channel.variant_attrs_unknown_hint') || '变体属性未知，请先配置变体属性后再添加子产品。'}</p>
                        </div>
                      </div>
                    )}
                    {/* Variant combinations table */}
                    {combinations.length > 0 && (
                      <div className="overflow-x-auto border dark:border-slate-700 rounded-xl shadow-sm">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
                            <tr>
                              <th className="p-3 font-medium whitespace-nowrap w-14" style={{width:56,minWidth:56}}>{t('products.image')}</th>
                              <th className="p-3 font-medium whitespace-nowrap">{(() => { const na = eavConfig.find(a => a.attribute_code === (standardCodes.name || prefixedCode('name')) || a.attribute_code === 'name'); return na ? getLocalizedLabel(na) : 'Name'; })()}</th>
                              <th className="p-3 font-medium whitespace-nowrap">{t('channel.sku')}</th>
                              <th className="p-3 font-medium whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <span>{(() => { const pa = eavConfig.find(a => a.attribute_code === (standardCodes.price || prefixedCode('price')) || a.attribute_code === 'price'); return pa ? getLocalizedLabel(pa) : 'Price'; })()}</span>
                                  {(() => { const pa = eavConfig.find(a => a.attribute_code === (standardCodes.price || prefixedCode('price')) || a.attribute_code === 'price'); const cu = pa?.currency_unit || pa?.default_currency; return cu ? <span className="text-xs text-slate-400 dark:text-slate-500 ml-1">({cu})</span> : null; })()}
                                </div>
                              </th>
                              {variantDefiningAttrs.map(a => (
                                <th key={a.code} className="p-3 font-medium whitespace-nowrap text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                  {a.label || a.code}
                                </th>
                              ))}
                              <th className="p-3 font-medium w-16">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y dark:divide-slate-700">
                            {combinations.map(c => {
                              const firstImage = c.images && c.images.length > 0 ? c.images[0] : null;
                              const isExistingChild = c._isExisting === true;
                              return (
                              <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                <td className="p-2 text-center" style={{width:56,minWidth:56}}>
                                  {firstImage ? (
                                    <img src={firstImage} alt="" className="w-10 h-10 object-cover rounded border border-slate-200 dark:border-slate-700" />
                                  ) : (
                                    <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-700"></div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {viewOnlyMode ? (
                                    <span className="text-sm px-1.5 py-1.5 block">{c.name}</span>
                                  ) : (
                                    <input
                                      className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                                      value={c.name}
                                      onChange={e => {
                                        const next = [...combinations];
                                        const idx = next.findIndex(x => x.id === c.id);
                                        if (idx >= 0) next[idx] = { ...next[idx], name: e.target.value };
                                        setCombinations(next);
                                      }}
                                    />
                                  )}
                                </td>
                                <td className="p-2">
                                  {viewOnlyMode ? (
                                    <span className="text-sm px-1.5 py-1.5 block">{c.sku}</span>
                                  ) : (
                                    <input
                                      className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                                      value={c.sku}
                                      onChange={e => {
                                        const next = [...combinations];
                                        const idx = next.findIndex(x => x.id === c.id);
                                        if (idx >= 0) next[idx] = { ...next[idx], sku: e.target.value };
                                        setCombinations(next);
                                      }}
                                    />
                                  )}
                                </td>
                                <td className="p-2">
                                  {viewOnlyMode ? (
                                    <span className="text-sm px-1.5 py-1.5 block">{c.price || ''}</span>
                                  ) : (
                                    <input
                                      className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                                      type="number" min="0" step="0.01"
                                      value={c.price}
                                      onChange={e => {
                                        const next = [...combinations];
                                        const idx = next.findIndex(x => x.id === c.id);
                                        if (idx >= 0) next[idx] = { ...next[idx], price: parseFloat(e.target.value) || 0 };
                                        setCombinations(next);
                                      }}
                                    />
                                  )}
                                </td>
                                {variantDefiningAttrs.map(a => (
                                  <td key={a.code} className="p-2">
                                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-md">
                                      {(() => { const v = c.attributes[a.code]; const at = variantAttrs.find(va => va.code === a.code); const o = at?.options?.find(opt => String(opt.value) === String(v)); return o ? o.label : (v || '-'); })()}
                                    </span>
                                  </td>
                                ))}
                                <td className="p-2 whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    {isExistingChild && (
                                      <>
                                        <button type="button" onClick={() => navigate(`/channel/eav-products/view/${c._ref}`)}
                                          className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                          title="View">
                                          <Eye size={15} />
                                        </button>
                                        {!viewOnlyMode && (
                                          <button type="button" onClick={() => navigate(`/channel/eav-products/edit/${c._ref}`)}
                                            className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                                            title="Edit">
                                            <Edit3 size={15} />
                                          </button>
                                        )}
                                      </>
                                    )}
                                    {!viewOnlyMode && (
                                      <button type="button" onClick={() => removeCombination(c.id)}
                                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                                        <Trash2 size={15} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {combinations.length === 0 && (
                      <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-6">
                        {t('channel.no_variants_yet')}
                      </p>
                    )}
                  </div>
                )}
                {/* ═══ M2 Media Gallery ═══ */}
                {formData.channel_type === 'magento2' &&
                  ((standardCodes.name ? attr.attribute_code === standardCodes.name : attr.attribute_code.replace(/^m2_\d+_/, '') === 'name')) && (
                  <div className="pt-6">
                    <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                      <div className="w-1 h-4 bg-purple-500 rounded-full"></div>
                      <ImageIcon size={18} className="text-purple-500" />
                      {t('m2_media.media_gallery')}
                    </h3>
                    <M2MediaGallery
                      entityId={isEdit && formData.entity_id ? parseInt(formData.entity_id, 10) : null}
                      viewOnly={!!viewOnlyMode}
                      onImagesChange={!id ? setPendingImages : undefined}
                    />
                  </div>
                )}
              </div>
            );})}
          </div>
        )}

        {/* ═══ Action buttons ═══ */}
        {!viewOnlyMode && (
          <div className="flex justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-700 mt-6">
            <button type="button" onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={isSubmitting}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                isSubmitting
                  ? 'bg-blue-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
              {isSubmitting ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : <Save size={18} />}
              {isSubmitting ? t('common.processing') : (isEdit ? t('common.update') : t('common.add'))}
            </button>
          </div>
        )}
      </form>

      {/* ═══════════════════════════════════════════════════════
         Configure Variants Modal
         ═══════════════════════════════════════════════════════ */}
      {showVariantConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-fadeIn">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Settings2 size={20} className="text-amber-500" />
                {t('channel.configure_variants')}
              </h2>
              <button type="button" onClick={() => setShowVariantConfig(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Step 1: Select variant-defining attributes */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('channel.select_variant_attrs')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {variantAttrs.length === 0 ? (
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      {t('channel.no_variant_attrs_in_set') || '当前属性集没有可用于配置变体的属性，请选择包含 Select 类属性的属性集'}
                    </p>
                  ) : (
                    variantAttrs.map(attr => {
                      const isSelected = variantDefiningAttrs.some(v => v.code === attr.code);
                      return (
                        <button key={attr.code} type="button"
                        onClick={() => {
                          if (isSelected) {
                            const { [attr.code]: _, ...rest } = variantValues;
                            setVariantValues(rest);
                            setVariantDefiningAttrs(prev => prev.filter(v => v.code !== attr.code));
                          } else {
                            setVariantDefiningAttrs(prev => [...prev, { code: attr.code, label: attr.label || attr.code }]);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          isSelected
                            ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600 text-amber-800 dark:text-amber-300'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-amber-300'
                        }`}>
                        {attr.label || attr.code}
                      </button>
                    );
                  }))}
                </div>
              </div>

              {/* Step 2: Select option values */}
              {variantDefiningAttrs.length > 0 && (
                <div className="space-y-6">
                  {variantDefiningAttrs.map(attr => {
                    const options = variantAttrOptions[attr.code];
                    const selected = variantValues[attr.code] || [];
                    return (
                      <div key={attr.code} className="space-y-2">
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {attr.label || attr.code}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {options === null ? (
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                              <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                              Loading...
                            </div>
                          ) : options && options.length > 0 ? (
                            options.map(opt => {
                              const isOptSelected = selected.includes(opt.value);
                              return (
                                <button key={opt.option_id || opt.value} type="button"
                                  onClick={() => toggleVariantOption(attr.code, opt.value)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                    isOptSelected
                                      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600 text-blue-800 dark:text-blue-300'
                                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-blue-300'
                                  }`}>
                                  {opt.label}
                                </button>
                              );
                            })
                          ) : (
                            <p className="text-xs text-slate-400">{t('channel.no_predefined_options')}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Preview */}
              {variantDefiningAttrs.length > 0 && Object.keys(variantValues).filter(k => variantValues[k].length > 0).length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('channel.preview_combinations')} ({generateCombinations().length})
                  </label>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <ul className="space-y-1">
                      {generateCombinations().map((combo, idx) => (
                        <li key={idx} className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                          {variantDefiningAttrs.map(a => {
                            const opt = (variantAttrOptions[a.code] || []).find(o => o.value === combo[a.code]);
                            return `${a.label || a.code}: ${opt ? opt.label : combo[a.code]}`;
                          }).join(' | ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Step 3: Image Mapping */}
              {variantDefiningAttrs.length > 0 && Object.keys(variantValues).filter(k => variantValues[k].length > 0).length > 0 && (
                <div className="space-y-4">
                  <hr className="border-slate-200 dark:border-slate-700" />
                  <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                    3. {t('products.variant_images_upload') || '上传子产品图片'}
                  </h3>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {t('products.select_image_attr') || '选择用于区分图片的属性'}
                    </label>
                    <select
                      className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-800"
                      value={variantImageAttr}
                      onChange={e => setVariantImageAttr(e.target.value)}
                    >
                      <option value="">{t('common.select')}</option>
                      {variantDefiningAttrs.map(attr => (
                        <option key={attr.code} value={attr.code}>{attr.label || attr.code}</option>
                      ))}
                    </select>
                  </div>

                  {variantImageAttr && variantAttrOptions[variantImageAttr] ? (
                    <div className="space-y-6 mt-2">
                      {variantAttrOptions[variantImageAttr]
                        .filter(opt => variantValues[variantImageAttr]?.includes(opt.value))
                        .map(opt => (
                          <div key={opt.option_id} className="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">
                              {opt.label}
                            </label>
                            <M2MediaGallery
                              entityId={null}
                              onImagesChange={(newImages) => {
                                setVariantImages(prev => ({ ...prev, [opt.value]: newImages }));
                              }}
                            />
                          </div>
                        ))}
                    </div>
                  ) : variantImageAttr ? (
                    <p className="text-sm text-slate-400">{t('common.loading')}</p>
                  ) : null}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowVariantConfig(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleConfirmVariants}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                <Plus size={16} />
                {t('channel.generate_variants')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
         Add Existing Variants Modal
         ═══════════════════════════════════════════════════════ */}
      {/* 更新变体属性弹窗 */}
      {showUpdateVariantAttrs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60" onClick={() => setShowUpdateVariantAttrs(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col animate-fadeIn" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                {t('channel.update_variant_attrs') || '更新变体属性'}
              </h2>
              <button type="button" onClick={() => setShowUpdateVariantAttrs(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                {t('channel.update_variant_attrs_hint') || '已选的变体属性不可取消。勾选新增的属性后保存，系统将自动验证所有子产品是否包含新属性的值。'}
              </p>
              {variantAttrs.filter(attr => {
                const code = attr.code || attr.attribute_code;
                // 只显示选项型属性（Dropdown / Multiple Select / Visual Swatch / Text Swatch）
                const inputType = attr.input_type || '';
                const allowedTypes = ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'];
                if (!allowedTypes.includes(inputType)) return false;
                // 如果在 eavConfig 中找到了此属性，说明属当前属性集的合法属性
                if (eavConfig && eavConfig.length > 0) {
                  return eavConfig.some(ea => ea.attribute_code === code);
                }
                return true;
              }).map(attr => {
                const code = attr.code || attr.attribute_code;
                const isExisting = variantDefiningAttrs.some(v => v.code === code);
                const isSelected = selectedUpdateAttrs.includes(code);
                return (
                  <label key={code} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${isExisting ? 'bg-blue-100 dark:bg-blue-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isExisting}
                      className="rounded"
                      onChange={e => {
                        if (isExisting) return;
                        setSelectedUpdateAttrs(prev =>
                          e.target.checked
                            ? [...prev, code]
                            : prev.filter(c => c !== code)
                        );
                      }}
                    />
                    <span className="text-sm">{attr.label || code}</span>
                    {isExisting && (
                      <span className="ml-auto text-xs text-blue-500">{t('channel.already_selected') || '已选'}</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowUpdateVariantAttrs(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                {t('common.cancel')}
              </button>
              <button type="button"
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/channel/eav/products/${id}/variant-config/add`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + localStorage.getItem('token'),
                      },
                      body: JSON.stringify({ variant_attributes: selectedUpdateAttrs }),
                    });
                    const data = await res.json();
                    if (data.status === 'success') {
                      const newDefiningAttrs = data.data.variant_attributes.map(code => ({
                        code,
                        label: (variantAttrs.find(a => (a.code || a.attribute_code) === code)?.label || code),
                      }));
                      setVariantDefiningAttrs(newDefiningAttrs);
                      setShowUpdateVariantAttrs(false);
                      notify(t('channel.update_variant_attrs_success') || '变体属性更新成功', 'success');
                    } else {
                      notify(data.message || t('common.update_failed'), 'error');
                    }
                  } catch (e) {
                    notify(t('common.network_error') || '网络错误', 'error');
                  }
                }}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2 transition-colors">
                <Save size={16} />
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddVariants && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-fadeIn">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Plus size={20} className="text-blue-500" />
                {t('channel.add_existing_variants')}
              </h2>
              <button type="button" onClick={() => { setShowAddVariants(false); setSelectedAddEntityIds([]); }}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={variantSearch}
                    onChange={e => setVariantSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSearchCandidates(); }}
                    placeholder={t('channel.search_variant_hint')}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    autoFocus />
                </div>
                <button type="button" onClick={handleSearchCandidates}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors">
                  <Search size={16} />
                </button>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1 border border-slate-100 dark:border-slate-700 rounded-lg p-2">
                {variantCandidates.length === 0 ? (
                  <p className="text-center py-4 text-sm text-slate-400">{t('channel.no_products_found')}</p>
                ) : (
                  variantCandidates.filter(c => !combinations.some(ex => ex._ref === c.entity_id)).map(c => {
                    const isSelected = selectedAddEntityIds.includes(c.entity_id);
                    const valid = isProductValidForVariant(c);
                    const reason = valid ? '' : getProductInvalidReason(c);
                    return (
                      <button key={c.entity_id} type="button"
                        onClick={() => {
                          if (!valid) return;
                          setSelectedAddEntityIds(prev =>
                            isSelected ? prev.filter(id => id !== c.entity_id) : [...prev, c.entity_id]
                          );
                        }}
                        title={reason || undefined}
                        className={`w-full text-left p-2.5 rounded-lg transition-all text-sm border flex items-center gap-3 ${
                          !valid ? 'opacity-50 cursor-not-allowed' :
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border-transparent'
                        }`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 dark:border-slate-500'
                        }`}>
                          {isSelected && <span className="text-white text-xs">✓</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 dark:text-slate-200 truncate">
                            {c.attributes?.name || c.sku}
                            {!valid && <span className="ml-2 text-red-500 inline-flex items-center gap-0.5 text-xs"><AlertTriangle size={12} />{t('common.incompatible')}</span>}
                          </div>
                          <div className="text-xs text-slate-400 font-mono">{c.sku}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => { setShowAddVariants(false); setSelectedAddEntityIds([]); }}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleConfirmAddVariants}
                disabled={selectedAddEntityIds.length === 0}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  selectedAddEntityIds.length === 0
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}>
                <Plus size={16} />
                {t('channel.add_variants', { count: selectedAddEntityIds.length })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelEavForm;
